<script setup>
/**
 * CheckoutView
 * Order summary page with client details, cart items, and pricing breakdown
 * Read-only view - editing requires returning to catalog
 */
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useCartStore } from '@/app/store/cart.js'
import { useAuthStore } from '@/app/store/auth.js'
import { useEmail } from '@/core/composables/useEmail.js'
import { usePdf } from '@/core/composables/usePdf.js'
import { useCloudFunctions } from '@/core/composables/useCloudFunctions.js'
import {
  generateNewOrderEmailHtml,
  generateOrderPdfHtml,
  generatePdfHeaderHtml,
  generateCustomerServicePdfHtml,
} from '@/core/utils/emailTemplates.js'
import { createOrder, formatOrderNumber } from '@/core/services/orders.js'
import { getEmailRecipients } from '@/core/services/emailSections.js'
import { useCatalogStore } from '@/app/store/catalog.js'
import { subscribeLevelsConfig, calculatePointsFromTotal } from '@/core/services/levels.js'
import { ROUTES, STORAGE_KEYS } from '@/core/config/constants.js'
import { formatPrice } from '@core/utils/formatting.js'
import { VAT_RATE } from '@core/config/pricingConstants.js'
import {
  requiresBoxMultiples,
  parseBoxSize,
} from '@/ui/components/catalog/item-dialog/itemDialogUtils.js'
import { useWhatsapp } from '@core/composables/useWhatsapp.js'
import { getWhatsappRecipients } from '@core/services/whatsappSections.js'

const router = useRouter()
const route = useRoute()
const isPreSale = computed(() => route.query.mode === 'pre-sale')
const cartStore = useCartStore()
const authStore = useAuthStore()
const catalogStore = useCatalogStore()
const { sendEmail, sending: sendingEmail } = useEmail()
const { generatePdfAsBase64, generating: generatingPdf } = usePdf()
const { callFunction } = useCloudFunctions()
const { sendToTargets } = useWhatsapp()

// User & Client info (declared early for stock check)
const { isAgent } = storeToRefs(authStore)

// Loading state
const showLoading = ref(false)
const loadingMessage = ref('מכין את ההזמנה...')

// Stock availability check (clients only)
const stockAvailability = ref({})
const stockLoading = ref(false)
const stockChecked = ref(false)
const showStockModal = ref(false)
const modalStockData = ref({})
const adjustedQuantities = ref({})
const originalCartQuantities = ref({})
const stockCheckingForModal = ref(false)
const shakingMakat = ref(null)

// Pre-sale obligo check
const obligoChecking = ref(false)
const obligoExceeded = ref(null) // { orderTotal, obligoRest, overage } when blocking
const obligoErrorVisible = ref(false)
const obligoAlertSent = ref(false) // idempotency: only fire WhatsApp alert once per session
const obligoAlertState = ref('pending') // 'pending' | 'agent' | 'team' | 'failed'

// Only claim the agent was notified once the server confirms it actually was
const obligoNoticeText = computed(() => {
  switch (obligoAlertState.value) {
    case 'agent':
      return 'הסוכן האחראי עודכן ויצור קשר בהקדם'
    case 'team':
      return 'צוות המכירות עודכן ויצור קשר בהקדם'
    case 'failed':
      return 'לא הצלחנו לעדכן את הסוכן — אנא פנה לשירות הלקוחות'
    default:
      return 'מעדכנים את הסוכן האחראי…'
  }
})

async function checkStockAvailability() {
  if (isAgent.value) return
  const makats = Object.keys(cartStore.linesByMakat)
  if (makats.length === 0) return

  stockLoading.value = true
  try {
    const result = await callFunction('priorityGetItemsStockBatch', { makats })
    if (result?.stock) {
      stockAvailability.value = result.stock
      // Fire-and-forget: send WhatsApp alerts for out-of-stock items
      sendOutOfStockAlerts(result.stock)
    }
  } catch (err) {
    console.error('Stock check failed:', err)
  } finally {
    stockLoading.value = false
    stockChecked.value = true
  }
}

async function sendOutOfStockAlerts(stock) {
  try {
    const recipients = await getWhatsappRecipients('inventoryAlert')
    if (!recipients || recipients.length === 0) return

    const outOfStockItems = Object.entries(stock)
      .filter(([_, available]) => available <= 0)
      .map(([makat, available]) => {
        const catalogItem = catalogStore.itemsByMakat.get(makat)
        return {
          makat,
          name: catalogItem?.name || makat,
          image: catalogItem?.image || null,
          stock: available,
        }
      })

    if (outOfStockItems.length === 0) return

    // Build consolidated message with client details once + items list
    const header = `\u26A0\uFE0F *התראת מלאי צ׳קאאוט*`
    const clientInfo = `לקוח: ${clientDisplayName.value}${clientMakat.value ? ` (${clientMakat.value})` : ''}\nמשתמש: ${userName.value}${clientPhone.value ? `\nטלפון: ${clientPhone.value}` : ''}`
    const itemsList = outOfStockItems
      .map((item, i) => `${i + 1}. ${item.name} (מק״ט: ${item.makat}) - מלאי: ${item.stock}`)
      .join('\n')
    const caption = `${header}\n\n${clientInfo}\n\nמוצרים חסרים:\n${itemsList}`

    // Send product images + consolidated text summary below
    const itemsWithImages = outOfStockItems.filter((item) => item.image)
    if (itemsWithImages.length === 0) return

    const images = itemsWithImages.map((item, i) => ({
      url: item.image,
      caption: i === 0 ? caption : '',
    }))

    for (const recipient of recipients) {
      sendToTargets(images, recipient.phone).catch((err) =>
        console.error('WhatsApp inventory alert failed:', err)
      )
    }
  } catch (err) {
    console.error('sendOutOfStockAlerts error:', err)
  }
}

function getLineStockWarning(makat) {
  if (!stockChecked.value || isAgent.value) return null
  const available = stockAvailability.value[makat]
  if (available === undefined) return null
  const requested = cartStore.linesByMakat[makat]?.qty || 0
  if (available <= 0) return { type: 'out-of-stock', text: 'אזל מהמלאי' }
  if (requested > available)
    return { type: 'insufficient', text: `ביקשת ${requested}, זמין רק ${available}` }
  return null
}

// ─── Stock modal logic ───

const modalItems = computed(() => {
  if (!showStockModal.value) return []

  const items = []
  const lines = cartStore.linesByMakat

  // Build lookup from pricedCartLines for display info
  const mainLineByMakat = {}
  for (const line of cartLines.value) {
    if (!line.isPromoLine && !line.isDiscountLine && !mainLineByMakat[line.makat]) {
      mainLineByMakat[line.makat] = line
    }
  }

  for (const makat of Object.keys(lines)) {
    const cartLine = lines[makat]
    const displayLine = mainLineByMakat[makat]
    const catalogItem = catalogStore.itemsByMakat.get(makat)
    const available = modalStockData.value[makat]
    const adjustedQty = adjustedQuantities.value[makat]
    // Use original qty (before any modal adjustments) for stock comparison
    const requestedQty = originalCartQuantities.value[makat] || cartLine.qty

    // BoxSize step logic
    const isOnlyBox = catalogItem ? requiresBoxMultiples(catalogItem) : false
    const boxSize = catalogItem ? parseBoxSize(catalogItem) : 0
    const step = isOnlyBox && boxSize > 0 ? boxSize : 1

    let status, effectiveQty

    if (available === undefined || available === null) {
      status = 'available'
      effectiveQty = requestedQty
    } else if (available <= 0) {
      status = 'out-of-stock'
      effectiveQty = 0
    } else {
      // Max valid qty: highest boxSize multiple that fits in available stock
      const maxValid = Math.floor(available / step) * step
      if (maxValid <= 0) {
        // Can't order any valid box multiple — treat as out-of-stock
        status = 'out-of-stock'
        effectiveQty = 0
      } else if (requestedQty > available) {
        effectiveQty = adjustedQty !== undefined ? adjustedQty : requestedQty
        status =
          adjustedQty !== undefined && adjustedQty <= maxValid && adjustedQty > 0
            ? 'adjusted'
            : 'over-stock'
      } else {
        status = 'available'
        effectiveQty = requestedQty
      }
    }

    if (status === 'available') continue

    const unitPrice = displayLine?.unitPricePreVatPreDeposit || 0
    const maxValidQty = available > 0 ? Math.floor(available / step) * step : 0

    items.push({
      makat,
      name: displayLine?.name || makat,
      image: displayLine?.image || null,
      requestedQty,
      available: available ?? null,
      status,
      effectiveQty,
      unitPrice,
      step,
      isOnlyBox,
      maxValidQty,
      lineTotal: status === 'adjusted' ? effectiveQty * unitPrice : 0,
    })
  }

  return items
})

const hasAvailableItems = computed(() => {
  // Cart items not shown in modal are available (they passed the stock check)
  const problematicCount = modalItems.value.length
  const totalCount = Object.keys(cartStore.linesByMakat).length
  if (totalCount > problematicCount) return true
  // Also true if any over-stock item was adjusted to a valid qty
  return modalItems.value.some((i) => i.status === 'adjusted')
})

const modalTotal = computed(() => {
  // Makats that will be removed on confirm (excluded from total)
  const excludedMakats = new Set(
    modalItems.value
      .filter((i) => i.status === 'out-of-stock' || i.status === 'over-stock')
      .map((i) => i.makat)
  )
  // Sum pricedCartLines for all non-excluded items (cart already reflects adjusted qtys)
  const preVat = cartLines.value
    .filter((line) => !excludedMakats.has(line.makat))
    .reduce((sum, line) => sum + line.unitPricePreVatPreDeposit * line.qty, 0)
  const vat = preVat * VAT_RATE
  return { preVat, vat, totalWithVat: preVat + vat }
})

async function checkObligoForPreSale() {
  if (!clientMakat.value) {
    obligoErrorVisible.value = true
    return false
  }

  obligoChecking.value = true
  showLoading.value = true
  loadingMessage.value = 'בודק אובליגו'

  try {
    const res = await callFunction('priorityGetClientObligo', {
      clientMakat: String(clientMakat.value),
    })

    if (!res?.ok) {
      obligoErrorVisible.value = true
      return false
    }

    // No obligo record in Priority (direct + merakez lookups both empty):
    // clients outside merakez 10000 have no obligo frame defined — allow them
    // to proceed. Merakez 10000 (or unknown merakez) stays fail-closed.
    if (!res.summary) {
      const merakez = res.merakez != null ? String(res.merakez).trim() : ''
      if (merakez && merakez !== '10000') {
        return true
      }
      obligoErrorVisible.value = true
      return false
    }

    if (typeof res.summary.obligoRest !== 'number') {
      obligoErrorVisible.value = true
      return false
    }

    const total = pricedTotals.value?.totalWithVat ?? 0
    const rest = res.summary.obligoRest

    if (total > rest) {
      obligoExceeded.value = {
        orderTotal: Math.round(total * 100) / 100,
        obligoRest: Math.round(rest * 100) / 100,
        overage: Math.round((total - rest) * 100) / 100,
      }

      // Notify the assigned agent via WhatsApp. Kept non-blocking so the modal
      // renders immediately; the notice line updates once the server answers.
      // Idempotency guard prevents duplicate sends if the user re-clicks "send".
      if (!obligoAlertSent.value) {
        obligoAlertSent.value = true
        obligoAlertState.value = 'pending'
        callFunction('sendObligoAlert', {
          clientMakat: String(clientMakat.value),
          orderTotal: obligoExceeded.value.orderTotal,
          obligoRest: obligoExceeded.value.obligoRest,
          overage: obligoExceeded.value.overage,
        })
          .then((res) => {
            if (res?.notifiedAgent) obligoAlertState.value = 'agent'
            else if (res?.alertSent) obligoAlertState.value = 'team'
            else {
              obligoAlertState.value = 'failed'
              console.error('Obligo WhatsApp alert not delivered:', res?.reason)
            }
          })
          .catch((err) => {
            console.error('Obligo WhatsApp alert failed:', err)
            obligoAlertState.value = 'failed'
            obligoAlertSent.value = false // allow retry on next attempt
          })
      }

      return false
    }

    return true
  } catch (err) {
    console.error('Obligo check failed:', err)
    obligoErrorVisible.value = true
    return false
  } finally {
    obligoChecking.value = false
    showLoading.value = false
  }
}

function goBackToCart() {
  obligoExceeded.value = null
  router.push({ name: ROUTES.CATALOG })
}

async function handleProceedClick() {
  // Pre-sale: validate client obligo before proceeding (applies to all roles)
  if (isPreSale.value) {
    const ok = await checkObligoForPreSale()
    if (!ok) return
  }

  // Agents bypass stock check entirely
  if (isAgent.value) {
    handleSubmit()
    return
  }

  stockCheckingForModal.value = true
  try {
    const makats = Object.keys(cartStore.linesByMakat)
    if (makats.length === 0) return

    const result = await callFunction('priorityGetItemsStockBatch', { makats })
    const stock = result?.stock || {}
    modalStockData.value = stock

    // Also update inline warnings
    stockAvailability.value = stock
    stockChecked.value = true

    const hasIssues = makats.some((makat) => {
      const avail = stock[makat]
      const requested = cartStore.linesByMakat[makat]?.qty || 0
      return avail !== undefined && (avail <= 0 || requested > avail)
    })

    if (hasIssues) {
      adjustedQuantities.value = {}
      // Save original quantities before any modal changes
      originalCartQuantities.value = {}
      for (const m of makats) {
        originalCartQuantities.value[m] = cartStore.linesByMakat[m]?.qty || 0
      }
      showStockModal.value = true
    } else {
      handleSubmit()
    }
  } catch (err) {
    console.error('Stock check for modal failed:', err)
    // On error, proceed without blocking
    handleSubmit()
  } finally {
    stockCheckingForModal.value = false
  }
}

function getModalItemMeta(makat) {
  const item = modalItems.value.find((i) => i.makat === makat)
  return item || { step: 1, maxValidQty: 0 }
}

function applyModalQty(makat, newQty) {
  adjustedQuantities.value[makat] = newQty
  // Sync with cart in real-time
  if (newQty > 0) {
    cartStore.setQty(makat, newQty)
  }
}

function incrementModalQty(makat) {
  const { step, maxValidQty, requestedQty } = getModalItemMeta(makat)
  const current = adjustedQuantities.value[makat] ?? requestedQty
  // If current is above max, snap down to maxValidQty first
  const next = current > maxValidQty ? maxValidQty : current + step
  if (next > maxValidQty) return
  applyModalQty(makat, next)
}

function decrementModalQty(makat) {
  const { step, requestedQty } = getModalItemMeta(makat)
  const current = adjustedQuantities.value[makat] ?? requestedQty
  const next = current - step
  if (next < step) return
  applyModalQty(makat, next)
}

function triggerShake(makat) {
  shakingMakat.value = makat
  setTimeout(() => {
    shakingMakat.value = null
  }, 400)
}

function commitModalInput(makat, event) {
  const { step, maxValidQty, isOnlyBox, effectiveQty } = getModalItemMeta(makat)
  const num = parseInt(event.target.value, 10)

  const revert = () => {
    event.target.value = String(effectiveQty)
    triggerShake(makat)
  }

  if (isNaN(num) || num <= 0) return revert()
  if (isOnlyBox && step > 1 && num % step !== 0) return revert()
  const clamped = Math.min(num, maxValidQty)
  if (clamped <= 0) return revert()
  applyModalQty(makat, clamped)
}

function removeModalItem(makat) {
  cartStore.removeItem(makat)
  delete adjustedQuantities.value[makat]
  delete originalCartQuantities.value[makat]
}

function confirmStockModal() {
  const items = [...modalItems.value]

  for (const item of items) {
    if (item.status === 'out-of-stock' || item.status === 'over-stock') {
      cartStore.removeItem(item.makat)
    }
    // 'adjusted' items are already synced to cart in real-time via applyModalQty
  }

  showStockModal.value = false

  if (cartStore.isEmpty) return
  handleSubmit()
}

function closeStockModal() {
  showStockModal.value = false
}

// Levels config for points calculation
const levelsConfig = ref(null)
let unsubscribeLevels = null

onUnmounted(() => {
  if (unsubscribeLevels) {
    unsubscribeLevels()
  }
})

// Redirect to catalog if cart is empty and subscribe to levels config
onMounted(() => {
  if (cartStore.isEmpty) {
    router.replace({ name: ROUTES.CATALOG })
    return
  }

  // Subscribe to levels config for points calculation
  unsubscribeLevels = subscribeLevelsConfig((config) => {
    if (config) {
      levelsConfig.value = config
    }
  })

  // Check stock availability for client users
  checkStockAvailability()
})

// User & Client info
const userProfile = computed(() => authStore.user.profile)
const userClient = computed(() => authStore.user.client)

// Cart data - using the same computed properties as CartDrawer (single source of truth)
const cartLines = computed(() => cartStore.pricedCartLines)
const pricedTotals = computed(() => cartStore.pricedTotals)
const depositSummary = computed(() => cartStore.depositSummary)
const uniqueCount = computed(() => cartStore.uniqueCount)
const itemsCount = computed(() => cartStore.itemsCount)

// Expected points based on order total
const expectedPoints = computed(() => {
  if (!levelsConfig.value?.conversion || !pricedTotals.value?.totalWithVat) {
    return 0
  }
  return calculatePointsFromTotal(pricedTotals.value.totalWithVat, levelsConfig.value.conversion)
})

// Navigate back to catalog
function goToCatalog() {
  router.push({ name: ROUTES.CATALOG })
}

// Resolve the effective client (auth client for regular users, linked client for agents)
const effectiveClient = computed(() => userClient.value || catalogStore.linkedClient)

// Build order data for email
function buildOrderData() {
  return {
    client: {
      name: clientDisplayName.value,
      makat: clientMakat.value,
      address: clientAddress.value,
      city: userClient.value?.city || '',
      phone: userClient.value?.phone,
      email: clientEmail.value,
    },
    items: cartLines.value,
    totals: pricedTotals.value,
    deposit: depositSummary.value,
    pointsToAward: expectedPoints.value,
    firstOrder: effectiveClient.value?.first_order === true,
  }
}

// Get catalog URL for email button
function getCatalogUrl() {
  const baseUrl = window.location.origin
  return `${baseUrl}/app/catalog`
}

// Handle submit - save order and redirect to payment (clients) or send email (agents)
async function handleSubmit() {
  const email = clientEmail.value
  if (!email) {
    alert('לא נמצא אימייל ללקוח. לא ניתן לשלוח הזמנה.')
    return
  }

  // Show loading overlay
  showLoading.value = true
  loadingMessage.value = 'מכין את ההזמנה...'

  try {
    const orderData = buildOrderData()
    // Capture the payment amount NOW, before any reactive state changes
    const paymentAmount = orderData.totals.totalWithVat
    let orderNumber = null
    let orderId = null
    // Save order to Firestore (only for non-agent users)
    if (!isAgent.value && clientMakat.value) {
      loadingMessage.value = isPreSale.value ? 'שומר הזמנה' : 'שומר את ההזמנה...'
      const result = await createOrder(clientMakat.value, {
        ...orderData,
        userUid: authStore.userId,
        userEmail: email,
        pre_sale: isPreSale.value,
      })
      orderNumber = result.orderNumber
      orderId = result.orderId

      // Send order confirmation email with PDF attachments
      loadingMessage.value = isPreSale.value ? 'שולח אישור הזמנה ללקוח' : 'שולח אישור הזמנה...'
      try {
        const orderDataWithNumber = { ...orderData, orderNumber }
        // For pre-sale: omit paymentUrl so the "לחץ לתשלום" CTA is not rendered.
        const emailHtml = isPreSale.value
          ? generateNewOrderEmailHtml(orderDataWithNumber, getCatalogUrl())
          : generateNewOrderEmailHtml(
              orderDataWithNumber,
              getCatalogUrl(),
              `${window.location.origin}/app/orders`
            )
        const emailSubject = `אישור הזמנה #${formatOrderNumber(orderNumber)} - ${clientDisplayName.value}`

        // Generate order PDF
        const pdfHtml = generateOrderPdfHtml(orderDataWithNumber)
        const pdfBase64 = await generatePdfAsBase64(
          pdfHtml,
          `הזמנה-${formatOrderNumber(orderNumber)}`,
          {},
          (pageNum, totalPages, dateStr) =>
            generatePdfHeaderHtml(pageNum, totalPages, dateStr, orderNumber)
        )

        // Generate customer service PDF
        const baseUrl = window.location.origin
        const servicePdfHtml = generateCustomerServicePdfHtml(baseUrl)
        const servicePdfBase64 = await generatePdfAsBase64(servicePdfHtml, 'מידע-ושירות-לקוחות')

        // Pre-sale: also CC admin recipients configured in the emails admin page.
        let presaleCc = []
        if (isPreSale.value) {
          try {
            presaleCc = await getEmailRecipients('preSaleOrder')
          } catch (e) {
            console.error('Failed to load preSaleOrder recipients:', e)
          }
        }

        await sendEmail({
          to: email,
          cc: presaleCc,
          subject: emailSubject,
          html: emailHtml,
          from: 'GoFood',
          attachments: [
            {
              filename: `הזמנה-${formatOrderNumber(orderNumber)}.pdf`,
              content: pdfBase64,
            },
            {
              filename: 'מידע-ושירות-לקוחות.pdf',
              content: servicePdfBase64,
            },
          ],
        })
      } catch (emailErr) {
        console.error('Failed to send order confirmation email:', emailErr)
        // Continue to payment - email failure should not block checkout
      }

      // Pre-sale flow: order saved & email sent — skip payment, show success page.
      if (isPreSale.value) {
        loadingMessage.value = 'ההזמנה נשלחה בהצלחה'
        localStorage.setItem(
          STORAGE_KEYS.PENDING_ORDER,
          JSON.stringify({
            orderId,
            orderNumber: formatOrderNumber(orderNumber),
            clientMakat: clientMakat.value,
            amount: paymentAmount,
            preSale: true,
            createdAt: new Date().toISOString(),
          })
        )
        cartStore.clearCart()
        showLoading.value = false
        router.push({ name: ROUTES.PAYMENT_SUCCESS })
        return
      }

      // For non-agent users: Generate payment link and redirect
      // Use the pre-captured paymentAmount to avoid reactivity issues
      loadingMessage.value = 'יוצר קישור לתשלום...'
      const paymentData = {
        client: {
          vat: userClient.value?.vat,
          makat: clientMakat.value,
          name: clientDisplayName.value,
        },
        orderUid: orderId,
        phone: userClient.value?.phone,
        amount: paymentAmount.toFixed(2),
      }

      const paymentResult = await callFunction('generateOrderPaymentLink', paymentData)

      if (paymentResult?.paymentLink) {
        loadingMessage.value = 'מעביר לדף התשלום...'

        // Save pending order info to localStorage for payment success page
        localStorage.setItem(
          STORAGE_KEYS.PENDING_ORDER,
          JSON.stringify({
            orderId,
            orderNumber: formatOrderNumber(orderNumber),
            clientMakat: clientMakat.value,
            amount: paymentAmount,
            createdAt: new Date().toISOString(),
          })
        )

        // Clear cart before redirect
        cartStore.clearCart()
        // Redirect to payment page
        window.location.href = paymentResult.paymentLink
        return
      } else {
        throw new Error('לא התקבל קישור לתשלום')
      }
    }

    // Agent flow: Send email with PDF
    // Add order number to order data for email/PDF
    const orderDataWithNumber = {
      ...orderData,
      orderNumber,
    }

    // Generate email HTML
    loadingMessage.value = 'יוצר את תוכן ההזמנה...'
    const emailHtml = generateNewOrderEmailHtml(orderDataWithNumber, getCatalogUrl())

    // Generate PDF from order data with repeating header on each page
    loadingMessage.value = 'יוצר קובץ PDF של ההזמנה...'
    const pdfHtml = generateOrderPdfHtml(orderDataWithNumber)
    const pdfBase64 = await generatePdfAsBase64(
      pdfHtml,
      `הזמנה-${orderNumber ? formatOrderNumber(orderNumber) : clientDisplayName.value}`,
      {},
      (pageNum, totalPages, dateStr) =>
        generatePdfHeaderHtml(pageNum, totalPages, dateStr, orderNumber)
    )

    // Generate customer service PDF
    loadingMessage.value = 'יוצר קובץ מידע שירות לקוחות...'
    const baseUrl = window.location.origin
    const servicePdfHtml = generateCustomerServicePdfHtml(baseUrl)
    const servicePdfBase64 = await generatePdfAsBase64(servicePdfHtml, 'מידע-ושירות-לקוחות')

    // Send email with PDF attachments
    loadingMessage.value = 'שולח את ההזמנה...'
    const emailSubject = orderNumber
      ? `אישור הזמנה #${formatOrderNumber(orderNumber)} - ${clientDisplayName.value}`
      : `הזמנה חדשה - ${clientDisplayName.value}`

    await sendEmail({
      to: email,
      subject: emailSubject,
      html: emailHtml,
      from: 'GoFood',
      attachments: [
        {
          filename: `הזמנה-${orderNumber ? formatOrderNumber(orderNumber) : clientMakat.value || 'לקוח'}.pdf`,
          content: pdfBase64,
        },
        {
          filename: 'מידע-ושירות-לקוחות.pdf',
          content: servicePdfBase64,
        },
      ],
    })

    loadingMessage.value = 'ההזמנה נשלחה בהצלחה!'

    // Note: first_order flag is now flipped server-side in the PayPlus callback
    // after successful payment, not at order creation time

    // Show success briefly then hide loading
    await new Promise((resolve) => setTimeout(resolve, 1500))
    showLoading.value = false

    // Clear cart after successful order
    cartStore.clearCart()

    // Navigate to catalog for agents
    router.push({ name: ROUTES.CATALOG })
  } catch (err) {
    console.error('Failed to process order:', err)
    showLoading.value = false
    alert('שגיאה בעיבוד ההזמנה. נסה שוב.')
  }
}

// Check if currently processing
const isProcessing = computed(() => sendingEmail.value || generatingPdf.value)

// Get display name for client
const clientDisplayName = computed(() => {
  if (userClient.value?.name) return userClient.value.name
  if (userProfile.value?.displayName) return userProfile.value.displayName
  return 'לקוח'
})

// Get client address
const clientAddress = computed(() => {
  const client = userClient.value
  if (!client) return null

  const parts = []
  if (client.street) parts.push(client.street)
  if (client.city) parts.push(client.city)
  return parts.length > 0 ? parts.join(', ') : null
})

// Get client makat
const clientMakat = computed(() => {
  return userClient.value?.makat || userProfile.value?.client_makat || null
})

// Get client phone
const clientPhone = computed(() => {
  return userClient.value?.phone || null
})

// Get logged-in username
const userName = computed(() => {
  return authStore.userDisplayName
})

// Get client email
const clientEmail = computed(() => {
  return userProfile.value?.email || userClient.value?.email || null
})
</script>

<template>
  <div class="checkout-view">
    <!-- Loading Overlay -->
    <Transition name="fade">
      <div v-if="showLoading" class="loading-overlay">
        <div class="loading-content">
          <div class="loading-logo">
            <img
              src="https://firebasestorage.googleapis.com/v0/b/gofood-il.appspot.com/o/logos%2Flogo_300.png?alt=media&token=21d7d83a-7562-462e-b340-24265c9fa915"
              alt="GoFood"
            />
          </div>
          <div class="loading-spinner">
            <svg viewBox="0 0 50 50" class="spinner">
              <circle cx="25" cy="25" r="20" fill="none" stroke-width="4"></circle>
            </svg>
          </div>
          <p class="loading-message">{{ loadingMessage }}</p>
        </div>
      </div>
    </Transition>

    <!-- Header -->
    <header class="checkout-header">
      <div class="header-spacer"></div>
      <h1 class="checkout-title">סיכום הזמנה</h1>
      <button class="back-btn" @click="goToCatalog">
        <span class="back-full">חזור לקטלוג</span>
        <span class="back-short">חזור</span>
      </button>
    </header>

    <div class="checkout-content">
      <!-- Client Details Section -->
      <section class="client-section">
        <h2 class="section-title">
          <svg
            viewBox="0 0 24 24"
            width="20"
            height="20"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
          <span>פרטי לקוח</span>
        </h2>
        <div class="client-card">
          <div class="client-info-row">
            <span class="client-label">שם:</span>
            <span class="client-value">{{ clientDisplayName }}</span>
          </div>
          <div v-if="clientMakat" class="client-info-row">
            <span class="client-label">מספר לקוח:</span>
            <span class="client-value">{{ clientMakat }}</span>
          </div>
          <div v-if="clientAddress" class="client-info-row">
            <span class="client-label">כתובת:</span>
            <span class="client-value">{{ clientAddress }}</span>
          </div>
          <div v-if="userClient?.phone" class="client-info-row">
            <span class="client-label">טלפון:</span>
            <span class="client-value" dir="ltr">{{ userClient.phone }}</span>
          </div>
          <div v-if="clientEmail" class="client-info-row">
            <span class="client-label">אימייל:</span>
            <span class="client-value" dir="ltr">{{ clientEmail }}</span>
          </div>
        </div>
      </section>

      <!-- Cart Items Section -->
      <section class="items-section">
        <h2 class="section-title">
          <svg
            viewBox="0 0 24 24"
            width="20"
            height="20"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <circle cx="9" cy="21" r="1" />
            <circle cx="20" cy="21" r="1" />
            <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6" />
          </svg>
          <span>פריטים בהזמנה</span>
          <span class="items-count">({{ uniqueCount }} פריטים, {{ itemsCount }} יחידות)</span>
          <span v-if="stockLoading" class="stock-loading-text">בודק זמינות מלאי...</span>
        </h2>

        <div class="items-list">
          <div v-for="line in cartLines" :key="line.displayKey" class="item-card-wrapper">
            <!-- Stock Warning (clients only) -->
            <div
              v-if="getLineStockWarning(line.makat)"
              class="stock-warning"
              :class="getLineStockWarning(line.makat).type"
            >
              <svg
                viewBox="0 0 24 24"
                width="14"
                height="14"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              <span>{{ getLineStockWarning(line.makat).text }}</span>
            </div>

            <div class="item-card">
              <!-- Product Image -->
              <div class="item-image">
                <img v-if="line.image" :src="line.image" :alt="line.name" />
                <div v-else class="no-image">
                  <svg
                    viewBox="0 0 24 24"
                    width="32"
                    height="32"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1"
                  >
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <polyline points="21 15 16 10 5 21" />
                  </svg>
                </div>
              </div>

              <!-- Product Details -->
              <div class="item-details">
                <div class="item-name">{{ line.name }}</div>
                <div class="item-makat">מק"ט: {{ line.makat }}</div>

                <!-- Promo Badge -->
                <div v-if="line.isPromoLine" class="promo-badge">
                  <svg
                    viewBox="0 0 24 24"
                    width="12"
                    height="12"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path
                      d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
                    />
                  </svg>
                  <span>{{ line.promo?.label || 'מבצע' }}</span>
                </div>

                <!-- Discount Badge -->
                <div v-if="line.isDiscountLine" class="discount-badge">
                  <svg
                    viewBox="0 0 24 24"
                    width="12"
                    height="12"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="8" y1="12" x2="16" y2="12" />
                  </svg>
                  <span>הנחת כמות {{ line.discount?.discountPercent }}%</span>
                </div>
              </div>

              <!-- Quantity & Price -->
              <div class="item-pricing">
                <div class="item-qty">{{ line.qty }} יח'</div>
                <div class="item-unit-price">
                  <span v-if="line.isPromoLine || line.isDiscountLine" class="original-price">
                    {{ formatPrice(line.catalogPrice) }} ₪
                  </span>
                  <span class="current-price"
                    >{{ formatPrice(line.unitPricePreVatPreDeposit) }} ₪</span
                  >
                </div>
                <div class="item-line-total">
                  {{ formatPrice(line.unitPricePreVatPreDeposit * line.qty) }} ₪
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Pricing Summary Section -->
      <section class="summary-section">
        <h2 class="section-title">
          <svg
            viewBox="0 0 24 24"
            width="20"
            height="20"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <line x1="12" y1="1" x2="12" y2="23" />
            <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
          </svg>
          <span>סיכום מחירים</span>
        </h2>

        <div class="summary-card">
          <!-- Promo Savings -->
          <div v-if="pricedTotals.promoSavingsPreVat > 0" class="summary-row savings-row">
            <span class="summary-label">
              <svg
                viewBox="0 0 24 24"
                width="14"
                height="14"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
                />
              </svg>
              חיסכון במבצעים
            </span>
            <span class="summary-value savings-value"
              >-{{ formatPrice(pricedTotals.promoSavingsPreVat) }} ₪</span
            >
          </div>

          <!-- Discount Savings -->
          <div v-if="pricedTotals.discountSavingsPreVat > 0" class="summary-row savings-row">
            <span class="summary-label">
              <svg
                viewBox="0 0 24 24"
                width="14"
                height="14"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="8" y1="12" x2="16" y2="12" />
              </svg>
              הנחות כמות
            </span>
            <span class="summary-value savings-value"
              >-{{ formatPrice(pricedTotals.discountSavingsPreVat) }} ₪</span
            >
          </div>

          <!-- Items Subtotal -->
          <div class="summary-row">
            <span class="summary-label">סה"כ פריטים</span>
            <span class="summary-value">{{ itemsCount }} יח'</span>
          </div>

          <!-- Deposit -->
          <div v-if="depositSummary.hasDeposit" class="summary-row deposit-row">
            <span class="summary-label">פיקדון ({{ depositSummary.depositUnits }} יח')</span>
            <span class="summary-value deposit-value"
              >{{ formatPrice(depositSummary.depositPreVat) }} ₪</span
            >
          </div>

          <!-- Total Before VAT -->
          <div class="summary-row subtotal-row">
            <span class="summary-label">סה"כ לפני מע"מ</span>
            <span class="summary-value">{{ formatPrice(pricedTotals.totalPreVat) }} ₪</span>
          </div>

          <!-- VAT -->
          <div class="summary-row vat-row">
            <span class="summary-label">מע"מ (18%)</span>
            <span class="summary-value">{{ formatPrice(pricedTotals.vatAmount) }} ₪</span>
          </div>

          <!-- Grand Total -->
          <div class="summary-row grand-total-row">
            <span class="summary-label">סה"כ לתשלום כולל מע"מ</span>
            <span class="summary-value grand-total-value"
              >{{ formatPrice(pricedTotals.totalWithVat) }} ₪</span
            >
          </div>

          <!-- Points Award -->
          <div v-if="expectedPoints > 0" class="summary-row points-row">
            <span class="summary-label points-label">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                <polygon
                  points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
                />
              </svg>
              נקודות שתזכה לקבל
            </span>
            <span class="summary-value points-value">{{ expectedPoints }} נקודות</span>
          </div>
        </div>
      </section>
    </div>

    <!-- Footer Actions -->
    <footer class="checkout-footer">
      <button class="back-to-catalog-btn" @click="goToCatalog">
        <svg
          viewBox="0 0 24 24"
          width="18"
          height="18"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M5 12h14M12 5l7 7-7 7" />
        </svg>
        <span>המשך קניות</span>
      </button>
      <button
        class="submit-btn"
        :disabled="isProcessing || stockCheckingForModal || obligoChecking"
        @click="handleProceedClick"
      >
        <span>{{
          obligoChecking
            ? 'בודק אובליגו...'
            : stockCheckingForModal
              ? 'בודק מלאי...'
              : isProcessing
                ? 'מעבד...'
                : isAgent
                  ? 'שלח הזמנה'
                  : isPreSale
                    ? 'סיום ושליחה'
                    : 'עבור לתשלום'
        }}</span>
        <svg
          viewBox="0 0 24 24"
          width="20"
          height="20"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M19 12H5M12 5l-7 7 7 7" />
        </svg>
      </button>
    </footer>

    <!-- Stock Availability Modal -->
    <Transition name="stock-modal">
      <div v-if="showStockModal" class="stock-modal-overlay" @click.self="closeStockModal">
        <div class="stock-modal">
          <div class="stock-modal-header">
            <svg
              viewBox="0 0 24 24"
              width="24"
              height="24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
              />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            <h3>שימו לב - חלק מהמוצרים אינם זמינים</h3>
            <button class="stock-modal-close" @click="closeStockModal">
              <svg
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          <p v-if="hasAvailableItems" class="stock-modal-desc">
            הסכום שיחויב כולל רק את המוצרים הזמינים. מוצרים שאזלו מהמלאי לא ייכללו בהזמנה. ניתן
            לעדכן כמויות עבור מוצרים שחרגו מהמלאי.
          </p>
          <p v-else class="stock-modal-desc stock-modal-desc--empty">
            כל המוצרים בהזמנה אינם זמינים כרגע. חזור לקטלוג לבחירת מוצרים זמינים.
          </p>

          <div class="stock-modal-items">
            <div
              v-for="item in modalItems"
              :key="item.makat"
              class="stock-modal-item"
              :class="{
                'stock-item--available': item.status === 'available' || item.status === 'adjusted',
                'stock-item--unavailable': item.status === 'out-of-stock',
                'stock-item--overstock': item.status === 'over-stock',
              }"
            >
              <div class="stock-item-icon">
                <svg
                  v-if="item.status === 'available' || item.status === 'adjusted'"
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                <svg
                  v-else
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </div>

              <div class="stock-item-info">
                <span
                  class="stock-item-name"
                  :class="{
                    'stock-strikethrough':
                      item.status === 'out-of-stock' || item.status === 'over-stock',
                  }"
                >
                  {{ item.name }}
                </span>
                <span class="stock-item-makat">מק"ט: {{ item.makat }}</span>
                <span
                  v-if="item.status === 'out-of-stock'"
                  class="stock-item-badge stock-badge--oos"
                >
                  אזל מהמלאי
                </span>
                <span
                  v-else-if="item.status === 'over-stock'"
                  class="stock-item-badge stock-badge--over"
                >
                  ביקשת {{ item.requestedQty }}, זמין רק {{ item.available }}
                </span>
              </div>

              <div class="stock-item-qty">
                <template v-if="item.status === 'over-stock' || item.status === 'adjusted'">
                  <span class="stock-qty-label">עדכן כמות:</span>
                  <div class="stock-qty-stepper" :class="{ shaking: shakingMakat === item.makat }">
                    <button
                      class="stock-stepper-btn"
                      :disabled="item.effectiveQty <= item.step"
                      @click="decrementModalQty(item.makat)"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        width="14"
                        height="14"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2.5"
                      >
                        <line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                    </button>
                    <input
                      type="text"
                      inputmode="numeric"
                      class="stock-qty-input"
                      :value="item.effectiveQty"
                      @blur="commitModalInput(item.makat, $event)"
                      @keydown.enter="$event.target.blur()"
                    />
                    <button
                      class="stock-stepper-btn"
                      :disabled="item.effectiveQty >= item.maxValidQty"
                      @click="incrementModalQty(item.makat)"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        width="14"
                        height="14"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2.5"
                      >
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                    </button>
                  </div>
                  <span class="stock-qty-hint">מתוך {{ item.available }}</span>
                  <span v-if="item.step > 1" class="stock-qty-hint">מארז: {{ item.step }} יח'</span>
                </template>
              </div>

              <!-- Remove button -->
              <button
                class="stock-item-remove"
                title="הסר מההזמנה"
                @click="removeModalItem(item.makat)"
              >
                <svg
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <polyline points="3 6 5 6 21 6" />
                  <path
                    d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"
                  />
                </svg>
              </button>
            </div>
          </div>

          <div class="stock-modal-footer">
            <div v-if="hasAvailableItems" class="stock-modal-totals">
              <div class="stock-modal-total-row">
                <span class="stock-modal-total-label">סה"כ לפני מע"מ</span>
                <span class="stock-modal-total-value">{{ formatPrice(modalTotal.preVat) }} ₪</span>
              </div>
              <div class="stock-modal-total-row">
                <span class="stock-modal-total-label">מע"מ (18%)</span>
                <span class="stock-modal-total-value">{{ formatPrice(modalTotal.vat) }} ₪</span>
              </div>
              <div class="stock-modal-total-row stock-modal-total-row--grand">
                <span class="stock-modal-total-label">סה"כ לתשלום</span>
                <span class="stock-modal-total-value stock-modal-grand-total"
                  >{{ formatPrice(modalTotal.totalWithVat) }} ₪</span
                >
              </div>
            </div>
            <div class="stock-modal-actions">
              <button class="stock-modal-btn stock-modal-btn--secondary" @click="closeStockModal">
                חזור לסיכום
              </button>
              <button
                v-if="hasAvailableItems"
                class="stock-modal-btn stock-modal-btn--primary"
                @click="confirmStockModal"
              >
                {{ isPreSale ? 'סיום ושליחה' : 'המשך לתשלום' }}
              </button>
              <button v-else class="stock-modal-btn stock-modal-btn--catalog" @click="goToCatalog">
                חזור לקטלוג
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Obligo Exceeded Modal (pre-sale only, hard block) -->
    <Transition name="obligo-exceeded">
      <div
        v-if="obligoExceeded !== null"
        class="obligo-exceeded-overlay"
        @click.self="obligoExceeded = null"
      >
        <div class="obligo-exceeded-modal" role="dialog" aria-modal="true">
          <button class="obligo-exceeded-close" aria-label="סגור" @click="obligoExceeded = null">
            <svg
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>

          <div class="obligo-exceeded-hero">
            <div class="obligo-exceeded-icon">
              <svg
                viewBox="0 0 24 24"
                width="32"
                height="32"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path
                  d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>
            <h3 class="obligo-exceeded-title">חריגה ממסגרת אובליגו</h3>
            <p class="obligo-exceeded-subtitle">לא ניתן להמשיך עם ההזמנה במצב הנוכחי</p>
          </div>

          <div class="obligo-exceeded-body">
            <div class="obligo-exceeded-breakdown">
              <div class="obligo-exceeded-row">
                <span class="obligo-exceeded-label">סכום ההזמנה</span>
                <span class="obligo-exceeded-value"
                  >{{ formatPrice(obligoExceeded.orderTotal) }} ₪</span
                >
              </div>
              <div class="obligo-exceeded-row">
                <span class="obligo-exceeded-label">יתרת אובליגו זמינה</span>
                <span class="obligo-exceeded-value"
                  >{{ formatPrice(obligoExceeded.obligoRest) }} ₪</span
                >
              </div>
              <div class="obligo-exceeded-divider"></div>
              <div class="obligo-exceeded-row obligo-exceeded-row--overage">
                <span class="obligo-exceeded-label">חריגה</span>
                <span class="obligo-exceeded-value obligo-exceeded-value--overage"
                  >{{ formatPrice(obligoExceeded.overage) }} ₪</span
                >
              </div>
            </div>

            <p class="obligo-exceeded-helper">
              להמשך ההזמנה יש להפחית את הסכום או לפנות לצוות המכירות.
            </p>

            <div
              class="obligo-exceeded-notice"
              :class="{ 'obligo-exceeded-notice--failed': obligoAlertState === 'failed' }"
            >
              <svg
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
              <span>{{ obligoNoticeText }}</span>
            </div>
          </div>

          <div class="obligo-exceeded-actions">
            <button
              class="obligo-exceeded-btn obligo-exceeded-btn--secondary"
              @click="obligoExceeded = null"
            >
              סגור
            </button>
            <button class="obligo-exceeded-btn obligo-exceeded-btn--primary" @click="goBackToCart">
              חזור לעגלה
            </button>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Obligo Verification Error Modal (pre-sale only, hard block) -->
    <Transition name="stock-modal">
      <div
        v-if="obligoErrorVisible"
        class="stock-modal-overlay"
        @click.self="obligoErrorVisible = false"
      >
        <div class="stock-modal obligo-modal">
          <div class="stock-modal-header">
            <svg
              viewBox="0 0 24 24"
              width="24"
              height="24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
              />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            <h3>שגיאה באימות אובליגו</h3>
            <button class="stock-modal-close" @click="obligoErrorVisible = false">
              <svg
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
          <p class="stock-modal-desc">לא הצלחנו לאמת את האובליגו, אנא צור קשר עם המכירות.</p>
          <div class="stock-modal-actions">
            <button
              class="stock-modal-btn stock-modal-btn--primary"
              @click="obligoErrorVisible = false"
            >
              הבנתי
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.checkout-view {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  background: var(--bg-secondary);
}

/* Header */
.checkout-header {
  direction: rtl;
  display: flex;
  align-items: stretch;
  background: var(--gradient-header);
  color: white;
  position: sticky;
  top: 0;
  z-index: 10;
  min-height: 56px;
}

.header-spacer {
  width: 100px;
}

.checkout-title {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-sm) var(--spacing-lg);
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  margin: 0;
}

.back-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 var(--spacing-lg);
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-left: 1px solid rgba(255, 255, 255, 0.15);
  color: white;
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
  white-space: nowrap;
}

.back-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.back-short {
  display: none;
}

/* Content */
.checkout-content {
  flex: 1;
  padding: var(--spacing-lg);
  max-width: 900px;
  margin: 0 auto;
  width: 100%;
}

/* Sections */
.client-section,
.items-section,
.summary-section {
  margin-bottom: var(--spacing-lg);
}

.section-title {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  margin: 0 0 var(--spacing-md);
}

.section-title svg {
  color: var(--color-primary);
}

.items-count {
  font-weight: var(--font-weight-normal);
  color: var(--text-muted);
  font-size: var(--font-size-sm);
}

/* Client Card */
.client-card {
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-md);
}

.client-info-row {
  display: flex;
  gap: var(--spacing-sm);
  padding: var(--spacing-xs) 0;
}

.client-info-row:not(:last-child) {
  border-bottom: 1px solid var(--border-color);
}

.client-label {
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary);
  min-width: 80px;
}

.client-value {
  color: var(--text-primary);
}

/* Items List */
.items-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.item-card {
  display: grid;
  grid-template-columns: 80px 1fr auto;
  gap: var(--spacing-md);
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-md);
  align-items: center;
}

.item-image {
  width: 80px;
  height: 80px;
  border-radius: var(--border-radius);
  overflow: hidden;
  background: var(--bg-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
}

.item-image img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.no-image {
  color: var(--text-muted);
  opacity: 0.5;
}

.item-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.item-name {
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  font-size: var(--font-size-base);
}

.item-makat {
  font-size: var(--font-size-xs);
  color: var(--text-muted);
}

.promo-badge,
.discount-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px var(--spacing-xs);
  border-radius: var(--border-radius-sm);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  width: fit-content;
}

.promo-badge {
  background: rgba(34, 197, 94, 0.1);
  color: #15803d;
}

.promo-badge svg {
  color: #22c55e;
}

.discount-badge {
  background: rgba(59, 130, 246, 0.1);
  color: #1d4ed8;
}

.discount-badge svg {
  color: #3b82f6;
}

.item-pricing {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  text-align: left;
}

.item-qty {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

.item-unit-price {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.original-price {
  font-size: var(--font-size-xs);
  color: var(--text-muted);
  text-decoration: line-through;
}

.current-price {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

.item-line-total {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
}

/* Summary Card */
.summary-card {
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-md);
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-sm) 0;
}

.summary-row:not(:last-child) {
  border-bottom: 1px solid var(--border-color);
}

.summary-label {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

.summary-value {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  color: var(--text-primary);
}

.savings-row .summary-label {
  color: #15803d;
}

.savings-row .summary-label svg {
  color: #22c55e;
}

.savings-value {
  color: #15803d !important;
  font-weight: var(--font-weight-bold) !important;
}

.deposit-row .summary-label {
  color: #7c3aed;
}

.deposit-value {
  color: #7c3aed !important;
}

.subtotal-row {
  border-top: 2px solid var(--border-color) !important;
  margin-top: var(--spacing-xs);
  padding-top: var(--spacing-md) !important;
}

.vat-row .summary-label {
  color: var(--text-muted);
}

.grand-total-row {
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(99, 102, 241, 0.1) 100%);
  margin: var(--spacing-sm) calc(-1 * var(--spacing-md));
  margin-bottom: calc(-1 * var(--spacing-md));
  padding: var(--spacing-md) !important;
  border-radius: 0 0 var(--border-radius-lg) var(--border-radius-lg);
  border-bottom: none !important;
}

.grand-total-row .summary-label {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
}

.grand-total-value {
  font-size: var(--font-size-2xl) !important;
  font-weight: var(--font-weight-bold) !important;
  color: var(--color-primary) !important;
}

/* Points Row */
.points-row {
  background: linear-gradient(135deg, rgba(251, 191, 36, 0.1) 0%, rgba(245, 158, 11, 0.15) 100%);
  margin: 0 calc(-1 * var(--spacing-md));
  margin-bottom: calc(-1 * var(--spacing-md));
  padding: var(--spacing-sm) var(--spacing-md) !important;
  border-radius: 0 0 var(--border-radius-lg) var(--border-radius-lg);
  border-bottom: none !important;
}

.grand-total-row:has(+ .points-row),
.summary-rows:has(.points-row) .grand-total-row {
  border-radius: 0;
  margin-bottom: 0;
}

.points-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: var(--font-weight-medium);
  color: #b45309;
}

.points-label svg {
  color: #f59e0b;
}

.points-value {
  font-weight: var(--font-weight-bold) !important;
  color: #b45309 !important;
}

/* Footer */
.checkout-footer {
  display: flex;
  gap: var(--spacing-md);
  padding: var(--spacing-md) var(--spacing-lg);
  background: var(--bg-primary);
  border-top: 1px solid var(--border-color);
  position: sticky;
  bottom: 0;
}

.back-to-catalog-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  color: var(--text-secondary);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.back-to-catalog-btn:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.submit-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-md) var(--spacing-lg);
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  border: none;
  border-radius: var(--border-radius);
  color: white;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
  box-shadow: 0 2px 8px rgba(34, 197, 94, 0.3);
}

.submit-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(34, 197, 94, 0.4);
}

/* Responsive - Tablet */
@media (max-width: 768px) {
  .checkout-header {
    min-height: 48px;
  }

  .checkout-title {
    font-size: var(--font-size-lg);
  }

  .header-spacer {
    display: none;
  }

  .back-btn {
    padding: 0 var(--spacing-md);
  }

  .checkout-content {
    padding: var(--spacing-md);
  }

  .item-card {
    grid-template-columns: 60px 1fr;
    grid-template-rows: auto auto;
  }

  .item-image {
    width: 60px;
    height: 60px;
    grid-row: 1 / 3;
  }

  .item-pricing {
    grid-column: 2;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding-top: var(--spacing-xs);
    border-top: 1px dashed var(--border-color);
  }

  .checkout-footer {
    flex-direction: column;
    padding: var(--spacing-md);
  }

  .back-to-catalog-btn {
    order: 2;
  }

  .submit-btn {
    order: 1;
  }
}

/* Responsive - Mobile */
@media (max-width: 480px) {
  .back-full {
    display: none;
  }

  .back-short {
    display: inline;
  }

  .section-title {
    font-size: var(--font-size-sm);
  }

  .items-count {
    display: none;
  }

  .item-name {
    font-size: var(--font-size-sm);
  }

  .grand-total-value {
    font-size: var(--font-size-xl) !important;
  }
}

/* Loading Overlay */
.loading-overlay {
  position: fixed;
  inset: 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-lg);
  padding: var(--spacing-xl);
}

.loading-logo {
  width: 100px;
  height: 100px;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.loading-logo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.loading-spinner {
  width: 60px;
  height: 60px;
}

.spinner {
  width: 100%;
  height: 100%;
  animation: rotate 2s linear infinite;
}

.spinner circle {
  stroke: var(--primary);
  stroke-linecap: round;
  stroke-dasharray: 90, 150;
  stroke-dashoffset: 0;
  animation: dash 1.5s ease-in-out infinite;
}

@keyframes rotate {
  100% {
    transform: rotate(360deg);
  }
}

@keyframes dash {
  0% {
    stroke-dasharray: 1, 150;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -35;
  }
  100% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -124;
  }
}

.loading-message {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-medium);
  color: var(--text-primary);
  text-align: center;
}

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Stock availability */
.stock-loading-text {
  font-size: var(--font-size-xs);
  color: var(--color-warning);
  font-weight: var(--font-weight-normal);
  margin-right: auto;
}

.item-card-wrapper {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  overflow: hidden;
}

.item-card-wrapper .item-card {
  border: none;
  border-radius: 0;
}

.stock-warning {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-xs) var(--spacing-md);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  background: rgba(239, 68, 68, 0.12);
  color: var(--color-error);
  border-bottom: 1px solid rgba(239, 68, 68, 0.2);
}

.stock-warning svg {
  flex-shrink: 0;
}

/* Stock Availability Modal */
.stock-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 2500;
}

.stock-modal {
  position: fixed;
  inset: 0;
  background: var(--bg-primary);
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  direction: rtl;
  z-index: 2500;
}

/* Desktop: centered dialog instead of fullscreen */
@media (min-width: 1025px) {
  .stock-modal-overlay {
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--spacing-md);
  }

  .stock-modal {
    position: relative;
    inset: auto;
    max-width: 560px;
    max-height: 85vh;
    height: auto;
    border-radius: var(--border-radius-lg);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  }
}

.stock-modal-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-md) var(--spacing-lg);
  border-bottom: 1px solid var(--border-color);
  color: var(--color-warning);
}

.stock-modal-header h3 {
  flex: 1;
  margin: 0;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
}

.stock-modal-close {
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 4px;
  border-radius: var(--border-radius-sm);
  transition: all var(--transition-fast);
}

.stock-modal-close:hover {
  color: var(--text-primary);
  background: var(--bg-secondary);
}

.stock-modal-desc {
  padding: var(--spacing-sm) var(--spacing-lg);
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  line-height: 1.6;
}

.stock-modal-desc--empty {
  color: var(--color-error);
  font-weight: var(--font-weight-medium);
}

.stock-modal-items {
  flex: 1;
  overflow-y: auto;
  padding: 0 var(--spacing-lg);
}

.stock-modal-item {
  display: grid;
  grid-template-columns: 28px 1fr auto auto 28px;
  gap: var(--spacing-sm);
  align-items: center;
  padding: var(--spacing-sm) 0;
  border-bottom: 1px solid var(--border-color);
  transition: background var(--transition-fast);
}

.stock-modal-item:last-child {
  border-bottom: none;
}

.stock-item-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.stock-item--available .stock-item-icon {
  color: var(--color-success);
}

.stock-item--unavailable .stock-item-icon,
.stock-item--overstock .stock-item-icon {
  color: var(--color-error);
}

.stock-item-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.stock-item-name {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.stock-item--available .stock-item-name {
  color: var(--color-success);
}

.stock-item--unavailable .stock-item-name,
.stock-item--overstock .stock-item-name {
  color: var(--color-error);
}

.stock-strikethrough {
  text-decoration: line-through;
}

.stock-item-makat {
  font-size: var(--font-size-xs);
  color: var(--text-muted);
}

.stock-item-badge {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  width: fit-content;
}

.stock-badge--oos,
.stock-badge--over {
  color: var(--color-error);
}

.stock-item-qty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.stock-qty-stepper {
  display: flex;
  align-items: center;
  gap: 2px;
}

.stock-stepper-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  background: var(--bg-secondary);
  color: var(--text-primary);
  cursor: pointer;
  transition: all var(--transition-fast);
  padding: 0;
}

.stock-stepper-btn:hover:not(:disabled) {
  background: var(--bg-tertiary);
  border-color: var(--color-primary);
}

.stock-stepper-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.stock-qty-input {
  width: 50px;
  height: 28px;
  padding: 2px 4px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  text-align: center;
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  color: var(--text-primary);
  background: var(--bg-primary);
}

.stock-qty-input:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 2px rgba(40, 53, 72, 0.1);
}

.stock-qty-label {
  font-size: var(--font-size-xs);
  color: var(--color-info);
  font-weight: var(--font-weight-medium);
}

.stock-qty-hint {
  font-size: 10px;
  color: var(--text-muted);
}

.stock-qty-text {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  white-space: nowrap;
}

.stock-item-price {
  text-align: left;
  min-width: 70px;
}

.stock-price-value {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
}

.stock-price-na {
  font-size: var(--font-size-sm);
  color: var(--text-muted);
}

/* Remove button */
.stock-item-remove {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: var(--border-radius-sm);
  background: none;
  color: var(--text-muted);
  cursor: pointer;
  transition: all var(--transition-fast);
  padding: 0;
}

.stock-item-remove:hover {
  color: var(--color-error);
  background: rgba(239, 68, 68, 0.1);
}

/* Shake animation */
.shaking {
  animation: shake 0.4s ease-in-out;
}

.shaking .stock-qty-input {
  border-color: var(--color-error);
}

@keyframes shake {
  0%,
  100% {
    transform: translateX(0);
  }
  20%,
  60% {
    transform: translateX(-4px);
  }
  40%,
  80% {
    transform: translateX(4px);
  }
}

.stock-modal-footer {
  padding: var(--spacing-md) var(--spacing-lg);
  border-top: 1px solid var(--border-color);
}

.stock-modal-totals {
  margin-bottom: var(--spacing-md);
  padding-bottom: var(--spacing-sm);
  border-bottom: 1px solid var(--border-color);
}

.stock-modal-total-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2px 0;
}

.stock-modal-total-row--grand {
  margin-top: var(--spacing-xs);
  padding-top: var(--spacing-xs);
  border-top: 1px solid var(--border-color);
}

.stock-modal-total-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary);
}

.stock-modal-total-value {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--text-primary);
}

.stock-modal-grand-total {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--color-primary);
}

.stock-modal-actions {
  display: flex;
  gap: var(--spacing-sm);
}

.stock-modal-btn {
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.stock-modal-btn--secondary {
  flex: 0 0 auto;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  color: var(--text-secondary);
}

.stock-modal-btn--secondary:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.stock-modal-btn--primary {
  flex: 1;
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  border: none;
  color: white;
  font-weight: var(--font-weight-bold);
}

.stock-modal-btn--primary:hover {
  box-shadow: 0 2px 8px rgba(34, 197, 94, 0.3);
}

.stock-modal-btn--catalog {
  flex: 1;
  background: var(--gradient-primary);
  border: none;
  color: white;
  font-weight: var(--font-weight-bold);
}

.stock-modal-btn--catalog:hover {
  box-shadow: 0 2px 8px rgba(30, 41, 59, 0.3);
}

@media (max-width: 768px) {
  .stock-modal-header {
    padding: var(--spacing-sm) var(--spacing-md);
  }

  .stock-modal-header h3 {
    font-size: var(--font-size-sm);
  }

  .stock-modal-desc {
    padding: var(--spacing-xs) var(--spacing-md);
    font-size: var(--font-size-xs);
  }

  .stock-modal-items {
    padding: 0 var(--spacing-md);
  }

  .stock-modal-footer {
    padding: var(--spacing-sm) var(--spacing-md);
  }

  .stock-modal-actions {
    flex-direction: column-reverse;
  }
}

/* Stock modal enter/leave transitions */
.stock-modal-enter-active {
  transition: opacity 0.3s ease;
}
.stock-modal-enter-active .stock-modal {
  transition: transform 0.3s cubic-bezier(0.22, 0.61, 0.36, 1);
}
.stock-modal-leave-active {
  transition: opacity 0.25s ease;
}
.stock-modal-leave-active .stock-modal {
  transition: transform 0.25s cubic-bezier(0.55, 0.06, 0.68, 0.19);
}
.stock-modal-enter-from,
.stock-modal-leave-to {
  opacity: 0;
}
/* Mobile/Tablet: slide up from bottom */
.stock-modal-enter-from .stock-modal,
.stock-modal-leave-to .stock-modal {
  transform: translateY(100%);
}
/* Desktop: scale in from center */
@media (min-width: 1025px) {
  .stock-modal-enter-from .stock-modal,
  .stock-modal-leave-to .stock-modal {
    transform: scale(0.9) translateY(20px);
  }
}

/* ===== Obligo Exceeded Modal (premium financial style) ===== */
.obligo-exceeded-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: 1rem;
}

.obligo-exceeded-modal {
  position: relative;
  background: var(--bg-primary);
  border-radius: 20px;
  width: 100%;
  max-width: 460px;
  overflow: hidden;
  box-shadow:
    0 24px 60px rgba(15, 23, 42, 0.28),
    0 8px 24px rgba(15, 23, 42, 0.15);
  display: flex;
  flex-direction: column;
}

.obligo-exceeded-close {
  position: absolute;
  top: 14px;
  inset-inline-start: 14px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease;
  z-index: 2;
}
.obligo-exceeded-close:hover {
  background: rgba(255, 255, 255, 0.22);
  color: #fff;
}

.obligo-exceeded-hero {
  background: var(--gradient-header);
  padding: 2rem 1.5rem 1.75rem;
  text-align: center;
  color: #fff;
}

.obligo-exceeded-icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 1rem;
  background: rgba(255, 255, 255, 0.12);
  border: 2px solid rgba(255, 255, 255, 0.25);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-error);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
}
.obligo-exceeded-icon svg {
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.25));
}

.obligo-exceeded-title {
  margin: 0 0 0.35rem;
  font-size: 1.25rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: #fff;
}

.obligo-exceeded-subtitle {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.78);
}

.obligo-exceeded-body {
  padding: 1.5rem;
}

.obligo-exceeded-breakdown {
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 14px;
  padding: 0.5rem 1rem;
}

.obligo-exceeded-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.85rem 0;
  gap: 1rem;
}

.obligo-exceeded-divider {
  height: 1px;
  background: var(--border-color);
  margin: 0 -1rem;
}

.obligo-exceeded-label {
  font-size: 0.9rem;
  color: var(--text-secondary);
  font-weight: 500;
}

.obligo-exceeded-value {
  font-size: 1rem;
  color: var(--text-primary);
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.obligo-exceeded-row--overage .obligo-exceeded-label {
  color: var(--text-primary);
  font-weight: 600;
}

.obligo-exceeded-value--overage {
  font-size: 1.35rem;
  font-weight: 700;
  color: var(--color-error);
  letter-spacing: -0.01em;
}

.obligo-exceeded-helper {
  margin: 1rem 0 0;
  font-size: 0.85rem;
  line-height: 1.5;
  color: var(--text-secondary);
  text-align: center;
}

.obligo-exceeded-notice {
  margin-top: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.7rem 0.9rem;
  background: rgba(59, 130, 246, 0.08);
  border: 1px solid rgba(59, 130, 246, 0.22);
  border-radius: 10px;
  color: var(--color-info);
  font-size: 0.85rem;
  font-weight: 500;
  line-height: 1.4;
}
.obligo-exceeded-notice--failed {
  background: rgba(239, 68, 68, 0.08);
  border-color: rgba(239, 68, 68, 0.22);
  color: var(--color-error);
}
.obligo-exceeded-notice svg {
  flex-shrink: 0;
}
.obligo-exceeded-notice span {
  color: var(--text-primary);
}

.obligo-exceeded-actions {
  display: flex;
  gap: 0.75rem;
  padding: 0 1.5rem 1.5rem;
}

.obligo-exceeded-btn {
  flex: 1;
  padding: 0.85rem 1rem;
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition:
    background 0.2s ease,
    transform 0.1s ease,
    box-shadow 0.2s ease;
  border: 1px solid transparent;
  font-family: inherit;
}

.obligo-exceeded-btn--secondary {
  background: var(--bg-primary);
  color: var(--text-secondary);
  border-color: var(--border-color);
}
.obligo-exceeded-btn--secondary:hover {
  background: var(--bg-secondary);
  color: var(--text-primary);
}

.obligo-exceeded-btn--primary {
  background: var(--color-primary);
  color: #fff;
  box-shadow: 0 2px 8px rgba(30, 41, 59, 0.25);
}
.obligo-exceeded-btn--primary:hover {
  background: var(--color-primary-hover);
  box-shadow: 0 4px 14px rgba(30, 41, 59, 0.35);
}
.obligo-exceeded-btn:active {
  transform: translateY(1px);
}

@media (max-width: 480px) {
  .obligo-exceeded-overlay {
    align-items: flex-end;
    padding: 0;
  }
  .obligo-exceeded-modal {
    max-width: 100%;
    border-radius: 20px 20px 0 0;
  }
  .obligo-exceeded-hero {
    padding: 1.75rem 1.25rem 1.5rem;
  }
  .obligo-exceeded-body {
    padding: 1.25rem;
  }
  .obligo-exceeded-actions {
    flex-direction: column-reverse;
    padding: 0 1.25rem 1.25rem;
  }
  .obligo-exceeded-value--overage {
    font-size: 1.25rem;
  }
}

/* Transitions */
.obligo-exceeded-enter-active {
  transition: opacity 0.25s ease;
}
.obligo-exceeded-enter-active .obligo-exceeded-modal {
  transition: transform 0.3s cubic-bezier(0.22, 0.61, 0.36, 1);
}
.obligo-exceeded-leave-active {
  transition: opacity 0.2s ease;
}
.obligo-exceeded-leave-active .obligo-exceeded-modal {
  transition: transform 0.2s cubic-bezier(0.55, 0.06, 0.68, 0.19);
}
.obligo-exceeded-enter-from,
.obligo-exceeded-leave-to {
  opacity: 0;
}
.obligo-exceeded-enter-from .obligo-exceeded-modal,
.obligo-exceeded-leave-to .obligo-exceeded-modal {
  transform: translateY(100%);
}
@media (min-width: 481px) {
  .obligo-exceeded-enter-from .obligo-exceeded-modal,
  .obligo-exceeded-leave-to .obligo-exceeded-modal {
    transform: scale(0.92) translateY(16px);
  }
}
</style>
